/* eslint-disable eslint-comments/no-restricted-disable, max-lines -- File is too complex to refactor right now. Should be handled later */
import { TextModifiersMenu } from "@circle-react-shared/uikit/TipTap/Extensions/TextModifiers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useFrameHotkeys } from "@circle-react/hooks/utils/useFrameHotkeys";
import type { Editor } from "@tiptap/core";
import { EditorContent } from "@tiptap/react";
import { isFunction, noop } from "lodash";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDropzone } from "react-dropzone";

import { t } from "@/i18n-js/instance";
import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";

import { DragIndicator } from "./AttachmentPreviews/DragIndicator";
import { ChatConfirmationModal } from "./ChatConfirmationModal/ChatConfirmationModal";
import { useChatConfirmationModal } from "./ChatConfirmationModal/useChatConfirmationModal";
import { EMPTY_EDITOR_BLOCKS, DEFAULT_CHAT_PROPS } from "./constants";
import { MenuBar } from "./MenuBar";
import { PreviewBlocks } from "./PreviewBlocks";
import { TipTapEditorContext } from "./TipTapEditorContext";
import type { TiptapEditorProps } from "./TiptapEditorProps.types";

import "./styles.scss";
import { useTiptapEditor } from "./useTiptapEditor";
import { doesEditorContainsEditedLabel, isImeComposing } from "./utilities";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    editedlabel: {
      addEditedLabelNode: () => ReturnType;
    };
  }
}

export const TiptapEditor = ({
  autofocus = false,
  chatProps = DEFAULT_CHAT_PROPS,
  chatRoomAccessArr = [],
  className,
  disabled = false,
  editable = true,
  editedAt,
  editorClassName,
  editorRef,
  isLiveStream = false,
  isUpdate = false,
  maxLength,
  menuBarComponent,
  menubarProps = {},
  messageBoxClassName,
  onBlur,
  onChange,
  onDelete,
  onFocus,
  onSubmit,
  placeholder = t("message_placeholder"),
  portalTargetId,
  rich_text_body = {},
  shouldDisableEntities = false,
  shouldDisableMentions = false,
  shouldLimitEntitiesToSpaces = false,
  shouldRefetch,
  shouldScrollIntoView,
  shouldShowPreviewBlocks = true,
  shouldShowTextModifiersMenu = true,
  shouldSupportCodeBlocks = false,
  shouldSupportHeadings = false,
  shouldSupportTables = false,
  showMenuBar = true,
  spaceId,
  submitOnEnter = true,
  type = "chat",
  value,
  isMediaUploadAllowed = true,
  ...passThroughProps
}: TiptapEditorProps) => {
  // Component wiring
  const MenuBarComponent = menuBarComponent || MenuBar;

  // Confirmation modal state
  const chatConfirmationModal = useChatConfirmationModal();
  const {
    setShouldShowPrivateMessageConfirmationOnSubmit,
    shouldShowPrivateMessageConfirmationOnSubmit,
    toggleConfirmationModal,
    togglePrivateMessageConfirmationModal,
  } = chatConfirmationModal;

  // TipTap editor and shared editor state
  const {
    addMessageAttachments,
    addToLocalSgidToObjectMap,
    arePopupsVisible,
    attachments,
    editor,
    editorHasNoContent,
    isFocused,
    isInFrame,
    linkPreviews,
    localSgidToObjectMap,
    messageAttachments,
    removeMessageAttachments,
    sanitizedInitialValue,
    setLinkPreviews,
    setLocalSgidToObjectMap,
    setMessageAttachments,
    sgidToObjectMap,
  } = useTiptapEditor({
    autofocus,
    chatProps,
    editable,
    editorClassName,
    maxLength,
    menubarProps,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    portalTargetId,
    rich_text_body,
    shouldDisableEntities,
    shouldDisableMentions,
    shouldLimitEntitiesToSpaces,
    shouldSupportCodeBlocks,
    shouldSupportHeadings,
    shouldSupportTables,
    type,
    value,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localAttachments, setLocalAttachments] = useState<any>(
    editable ? attachments : [],
  );
  const shouldShowEditedLabel = Boolean(editedAt);

  const isUploading = localAttachments.some((a: any) => a.progress >= 0);

  // Submit flow
  const resetEditor = () => {
    editor?.commands.clearContent(false);
    setLocalAttachments([]);
    setLocalSgidToObjectMap({});
    setMessageAttachments([]);
    setLinkPreviews({});
  };

  const handleSubmit = async (skipConfirmationModal = false): Promise<void> => {
    const isEditorEmpty = editorHasNoContent() && !localAttachments.length;
    if ((isEditorEmpty && !isUpdate) || isUploading) return;
    if (isEditorEmpty && isUpdate) {
      return onDelete?.();
    }
    const mentions = Object.values(localSgidToObjectMap);
    const hasGroupMentions = mentions.some(
      (mention: any) => mention.type == "group_mention",
    );
    if (!skipConfirmationModal && hasGroupMentions) {
      toggleConfirmationModal();
      return;
    }
    if (shouldShowPrivateMessageConfirmationOnSubmit) {
      togglePrivateMessageConfirmationModal();
      return;
    }
    if (isFunction(onSubmit)) {
      const submissionPromise = onSubmit({
        body: editorHasNoContent() ? EMPTY_EDITOR_BLOCKS : editor?.getJSON(),
        attachments: localAttachments.map(({ signed_id }: any) => signed_id),
        localAttachments,
        localSgidToObjectMap,
        unfurlUrls: linkPreviews,
      });
      try {
        setIsSubmitting(true);
        // eslint-disable-next-line @typescript-eslint/await-thenable -- disabled for TypeScript v5 fix. Review when possible.
        await submissionPromise;
      } catch (e) {
        console.error(e);
      } finally {
        setIsSubmitting(false);
        resetEditor();
      }
    }
  };

  // Keyboard submit handling
  const isChineseOrJapaneseCharacter = (editor: Editor | null) => {
    if (!editor) return false;
    const text = editor.getText();

    const regex =
      /[\u3000-\u303F]|[\u3040-\u309F]|[\u30A0-\u30FF]|[\uFF00-\uFFEF]|[\u4E00-\u9FAF]|[\u2605-\u2606]|[\u2190-\u2195]|\u203B/; // Ref https://gist.github.com/ryanmcgrath/982242

    return regex.test(text);
  };

  useFrameHotkeys(
    "enter",
    event => {
      if (isImeComposing(event)) return;
      void handleSubmit();
    },
    {
      enabled:
        isFocused &&
        submitOnEnter &&
        !arePopupsVisible &&
        !isMobile &&
        !isChineseOrJapaneseCharacter(editor),
      enableOnContentEditable: true,
      ignoreEventWhen: isImeComposing,
    },
    [
      editor,
      localAttachments,
      arePopupsVisible,
      isFocused,
      shouldShowPrivateMessageConfirmationOnSubmit,
      linkPreviews,
    ],
  );

  // File uploads
  const addFilesToAttachments = (files: any) =>
    setLocalAttachments((prevAttachments: any) => [
      ...prevAttachments,
      ...files.map((file: any) => ({
        file,
        url: URL.createObjectURL(file),
        filename: file.name,
        byte_size: file.size,
        content_type: file.type,
        toUpload: true,
        progress: 0,
      })),
    ]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    onDrop: isMediaUploadAllowed ? addFilesToAttachments : noop,
    noClick: true,
    disabled: !isMediaUploadAllowed,
  });

  const fileInputProps: any = getInputProps();

  // Read-only editor sync
  useEffect(() => {
    if (shouldRefetch && !editable && editor) {
      editor.commands.setContent(sanitizedInitialValue, { emitUpdate: false }); // Re rending editor after the initial value is changed on condition
    }
    if (
      !editable &&
      shouldShowEditedLabel &&
      editor &&
      "addEditedLabelNode" in editor.commands &&
      typeof editor.commands.addEditedLabelNode === "function"
    ) {
      requestAnimationFrame(() => {
        if (!doesEditorContainsEditedLabel(editor)) {
          editor.chain().focus("end").addEditedLabelNode().run();
        }
      });
    }
  }, [
    sanitizedInitialValue,
    editor,
    shouldRefetch,
    shouldShowEditedLabel,
    editable,
  ]);

  if (!editor) {
    return null;
  }

  if (editorRef) {
    editorRef.current = editor;
  }

  // Context for TipTap node views and nested controls
  const contextValue: TipTapEditorContext = {
    addMessageAttachments,
    addToLocalSgidToObjectMap,
    attachments,
    chatProps,
    chatRoomAccessArr,
    disableAttachmentClicks: isInFrame,
    disableMentionClicks: isInFrame,
    editedAt,
    editor,
    isUpdate,
    localAttachments,
    localSgidToObjectMap,
    messageAttachments,
    portalTargetId,
    removeMessageAttachments,
    setLocalAttachments,
    setShouldShowPrivateMessageConfirmationOnSubmit,
    sgidToObjectMap,
    shouldRefetch,
    shouldScrollIntoView,
    spaceId,
    type,
    linkPreviews,
    setLinkPreviews,
  };

  // Render helpers
  const charactersCount = editor?.storage?.characterCount?.characters() || 0;
  const countText = `${String(charactersCount)}/${String(maxLength)}`;
  const messageBoxStyles =
    "bg-primary border border-primary shadow-xs rounded-xl hover:border-hover mb-4";

  return (
    <TipTapEditorContext.Provider value={contextValue}>
      {!editor.isEditable ? (
        <>
          {!editorHasNoContent() && <EditorContent editor={editor} />}
          {shouldShowPreviewBlocks && !isUpdate && <PreviewBlocks />}
        </>
      ) : (
        <div
          className={classNames(
            "text-dark relative flex flex-col px-4 py-3 text-sm transition-colors",
            messageBoxStyles,
            {
              "border-none": isDragActive,
              "border-hover shadow-none": !editorHasNoContent(),
              "pointer-events-none opacity-70": disabled || isSubmitting,
              "!bg-primary": isUpdate,
            },
            className,
          )}
          {...passThroughProps}
          {...getRootProps()}
          onPaste={e => {
            if (isMediaUploadAllowed && e.clipboardData?.files?.length > 0) {
              e.preventDefault();
              const files = Array.from(e.clipboardData.files);
              addFilesToAttachments(files);
            }
          }}
          data-testid="tiptap-editor"
        >
          {isDragActive && <DragIndicator />}
          <input {...fileInputProps} />
          <div
            className={classNames(
              anchorClassNames("chat-message-word-wrap"),
              "max-h-[300px] cursor-text overflow-auto",
              messageBoxClassName,
            )}
          >
            <EditorContent editor={editor} />
            {maxLength && <div className="text-light">{countText}</div>}
          </div>
          {editor && shouldShowTextModifiersMenu && (
            <TextModifiersMenu
              editor={editor}
              allowBubbleMenuOverflow={type !== "chat"}
            />
          )}
          {shouldShowPreviewBlocks && <PreviewBlocks />}
          {showMenuBar && (
            <MenuBarComponent
              isSubmitting={isSubmitting}
              isUploading={isUploading}
              openFilePicker={openFilePicker}
              editor={editor}
              handleSubmit={handleSubmit}
              editorHasNoContent={() =>
                isUpdate
                  ? false
                  : editorHasNoContent() && !localAttachments.length
              }
              isFocused={isFocused}
              isUpdate={isUpdate}
              isLiveStream={isLiveStream}
              portalTargetId={portalTargetId}
              {...menubarProps}
            />
          )}
        </div>
      )}
      <ChatConfirmationModal
        {...chatConfirmationModal}
        handleSubmit={handleSubmit}
        chatParticipantsCount={chatProps.chatParticipantsCount ?? 0}
      />
    </TipTapEditorContext.Provider>
  );
};
